import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import { message } from 'antd';
import {
  ArrowLeft, Plus, Trash2, Upload, Link, X, ChevronRight,
  LayoutGrid, Edit3, Check, Image, Layers,
} from 'lucide-react';
import { colors, spacing, radius, typography, transitions, gradients } from '../../../styles/theme';
import brandAssetActions from '../../../actions/brandAssets';
import brandDnaActions from '../../../actions/brandDna';

const captionStatusColor = { pending: '#8c8c8c', processing: '#fa8c16', done: '#52c41a', failed: '#f5576c' };

export default function BrandMoodboardsScreen() {
  const { brandId } = useParams();
  const navigate = useNavigate();

  const [brand, setBrand] = useState(null);
  const [moodboards, setMoodboards] = useState([]);
  const [activeMoodboard, setActiveMoodboard] = useState(null);
  const [loadingMoodboards, setLoadingMoodboards] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchBrand = useCallback(async () => {
    try {
      const res = await brandDnaActions.getBrand(brandId);
      if (res.success) setBrand(res.data);
    } catch { navigate('/brand-dna'); }
  }, [brandId, navigate]);

  const fetchMoodboards = useCallback(async () => {
    try {
      setLoadingMoodboards(true);
      const res = await brandAssetActions.listMoodboards(brandId);
      if (res.success) setMoodboards(res.data || []);
    } catch { /* silent */ } finally { setLoadingMoodboards(false); }
  }, [brandId]);

  const fetchDetail = useCallback(async (moodboardId) => {
    try {
      setLoadingDetail(true);
      const res = await brandAssetActions.getMoodboard(brandId, moodboardId);
      if (res.success) setActiveMoodboard(res.data);
    } catch { message.error('Failed to load moodboard'); } finally { setLoadingDetail(false); }
  }, [brandId]);

  useEffect(() => { fetchBrand(); fetchMoodboards(); }, [fetchBrand, fetchMoodboards]);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const res = await brandAssetActions.createMoodboard(brandId, { title: newTitle.trim(), description: newDesc.trim() || null });
      if (res.success) {
        setMoodboards((prev) => [...prev, { ...res.data, item_count: 0 }]);
        setNewTitle(''); setNewDesc(''); setShowCreate(false);
        fetchDetail(res.data.moodboard_id);
        message.success('Moodboard created');
      }
    } catch { message.error('Failed to create moodboard'); } finally { setCreating(false); }
  };

  const handleDelete = async (moodboardId, e) => {
    e.stopPropagation();
    try {
      await brandAssetActions.deleteMoodboard(brandId, moodboardId);
      setMoodboards((prev) => prev.filter((m) => m.moodboard_id !== moodboardId));
      if (activeMoodboard?.moodboard_id === moodboardId) setActiveMoodboard(null);
      message.success('Moodboard deleted');
    } catch { message.error('Failed to delete moodboard'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100vh', background: colors.background.primary }}>
      {/* Header */}
      <div style={{ padding: `${spacing.md}px ${spacing.xl}px`, borderBottom: `1px solid ${colors.border.secondary}`, display: 'flex', alignItems: 'center', gap: spacing.sm, background: colors.background.secondary }}>
        <motion.div whileHover={{ x: -2 }} onClick={() => navigate(`/brand-dna/${brandId}`)} style={{ display: 'flex', alignItems: 'center', gap: 6, color: colors.text.tertiary, fontSize: typography.fontSize.sm, cursor: 'pointer' }}>
          <ArrowLeft size={15} />
          {brand?.brand_name || 'Brand'}
        </motion.div>
        <ChevronRight size={13} color={colors.text.disabled} />
        <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.text.primary, display: 'flex', alignItems: 'center', gap: 6 }}>
          <LayoutGrid size={15} color={colors.brand.secondary} /> Moodboards
        </span>
        <div style={{ marginLeft: 'auto' }}>
          <motion.button whileHover={{ opacity: 0.85 }} whileTap={{ scale: 0.97 }} onClick={() => setShowCreate(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: gradients.primary, border: 'none', borderRadius: radius.sm, color: '#fff', fontSize: typography.fontSize.sm, cursor: 'pointer' }}>
            <Plus size={13} /> New Moodboard
          </motion.button>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left: Moodboard List */}
        <div style={{ width: 260, borderRight: `1px solid ${colors.border.secondary}`, display: 'flex', flexDirection: 'column', flexShrink: 0, overflowY: 'auto' }}>
          {/* Create form */}
          <AnimatePresence>
            {showCreate && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ overflow: 'hidden', borderBottom: `1px solid ${colors.border.secondary}`, background: colors.background.tertiary }}>
                <div style={{ padding: spacing.md }}>
                  <div style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: colors.text.secondary, marginBottom: spacing.xs }}>New Moodboard</div>
                  <input
                    autoFocus
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); if (e.key === 'Escape') { setShowCreate(false); setNewTitle(''); setNewDesc(''); } }}
                    placeholder="Campaign title…"
                    style={{ width: '100%', padding: '7px 10px', marginBottom: spacing.xs, background: colors.background.secondary, border: `1px solid ${colors.border.primary}`, borderRadius: radius.sm, color: colors.text.primary, fontSize: typography.fontSize.sm, outline: 'none', boxSizing: 'border-box' }}
                  />
                  <textarea
                    value={newDesc}
                    onChange={(e) => setNewDesc(e.target.value)}
                    placeholder="Description (optional)…"
                    rows={2}
                    style={{ width: '100%', padding: '7px 10px', marginBottom: spacing.sm, background: colors.background.secondary, border: `1px solid ${colors.border.primary}`, borderRadius: radius.sm, color: colors.text.primary, fontSize: typography.fontSize.sm, outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                  />
                  <div style={{ display: 'flex', gap: spacing.xs }}>
                    <motion.button whileHover={{ opacity: 0.85 }} whileTap={{ scale: 0.97 }} onClick={handleCreate} disabled={creating || !newTitle.trim()} style={{ flex: 1, padding: '6px 0', background: colors.brand.primary, border: 'none', borderRadius: radius.sm, color: '#fff', fontSize: typography.fontSize.xs, cursor: 'pointer', opacity: creating ? 0.6 : 1 }}>
                      {creating ? '…' : 'Create'}
                    </motion.button>
                    <motion.button whileHover={{ opacity: 0.7 }} onClick={() => { setShowCreate(false); setNewTitle(''); setNewDesc(''); }} style={{ flex: 1, padding: '6px 0', background: 'transparent', border: `1px solid ${colors.border.primary}`, borderRadius: radius.sm, color: colors.text.tertiary, fontSize: typography.fontSize.xs, cursor: 'pointer' }}>
                      Cancel
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {loadingMoodboards ? (
            <div style={{ padding: spacing.md }}>
              {[...Array(4)].map((_, i) => <div key={i} style={{ height: 60, borderRadius: radius.md, background: colors.surface.primary, marginBottom: spacing.xs, animation: 'pulse 1.5s ease-in-out infinite' }} />)}
              <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
            </div>
          ) : moodboards.length === 0 ? (
            <div style={{ padding: spacing.xl, textAlign: 'center', color: colors.text.disabled, fontSize: typography.fontSize.sm }}>
              <LayoutGrid size={32} color={colors.text.disabled} style={{ marginBottom: spacing.sm }} />
              <div>No moodboards yet</div>
              <div style={{ fontSize: typography.fontSize.xs, marginTop: 4 }}>Create one to get started</div>
            </div>
          ) : (
            <div style={{ padding: spacing.xs }}>
              {moodboards.map((m) => (
                <MoodboardRow key={m.moodboard_id} moodboard={m} active={activeMoodboard?.moodboard_id === m.moodboard_id} onClick={() => fetchDetail(m.moodboard_id)} onDelete={(e) => handleDelete(m.moodboard_id, e)} />
              ))}
            </div>
          )}
        </div>

        {/* Right: Moodboard Detail */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {activeMoodboard ? (
            <MoodboardDetail
              key={activeMoodboard.moodboard_id}
              brandId={brandId}
              moodboard={activeMoodboard}
              loading={loadingDetail}
              onRefresh={() => { fetchDetail(activeMoodboard.moodboard_id); fetchMoodboards(); }}
              onTitleUpdate={(newData) => {
                setActiveMoodboard((prev) => ({ ...prev, ...newData }));
                setMoodboards((prev) => prev.map((m) => m.moodboard_id === newData.moodboard_id ? { ...m, ...newData } : m));
              }}
            />
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: spacing.md, color: colors.text.disabled }}>
              <LayoutGrid size={48} color={colors.text.disabled} />
              <div style={{ fontSize: typography.fontSize.md }}>Select a moodboard</div>
              <div style={{ fontSize: typography.fontSize.sm }}>or create a new one</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MoodboardRow({ moodboard, active, onClick, onDelete }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div whileTap={{ scale: 0.98 }} onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, padding: `${spacing.sm}px ${spacing.md}px`, borderRadius: radius.md, background: active ? `${colors.brand.secondary}15` : hovered ? colors.surface.primary : 'transparent', border: active ? `1px solid ${colors.brand.secondary}40` : '1px solid transparent', cursor: 'pointer', transition: transitions.fast, marginBottom: 2 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: active ? colors.brand.secondary : colors.text.primary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{moodboard.title}</div>
        {moodboard.description && <div style={{ fontSize: typography.fontSize.xs, color: colors.text.disabled, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>{moodboard.description}</div>}
      </div>
      <span style={{ fontSize: typography.fontSize.xs, color: colors.text.disabled, flexShrink: 0 }}>{moodboard.item_count || 0}</span>
      {hovered && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={onDelete} style={{ color: colors.status.error, cursor: 'pointer', display: 'flex', flexShrink: 0 }}>
          <Trash2 size={13} />
        </motion.div>
      )}
    </motion.div>
  );
}

function MoodboardDetail({ brandId, moodboard, loading, onRefresh, onTitleUpdate }) {
  const [addTab, setAddTab] = useState('file'); // 'file' | 'url' | 'assets'
  const [urlInput, setUrlInput] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(moodboard.title);
  const fileRef = useRef();

  // Asset picker state
  const [assetPickerItems, setAssetPickerItems] = useState([]);
  const [selectedAssetIds, setSelectedAssetIds] = useState(new Set());
  const [loadingAssets, setLoadingAssets] = useState(false);

  useEffect(() => {
    if (addTab !== 'assets' || !showAdd) return;
    setLoadingAssets(true);
    brandAssetActions.listAssets(brandId, { limit: 100 })
      .then((res) => {
        if (res.success) setAssetPickerItems(res.data?.assets || res.data || []);
      })
      .catch(() => message.error('Failed to load assets'))
      .finally(() => setLoadingAssets(false));
  }, [addTab, showAdd, brandId]);

  const toggleAsset = (assetId) => {
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      next.has(assetId) ? next.delete(assetId) : next.add(assetId);
      return next;
    });
  };

  const handleAddFromAssets = async () => {
    if (!selectedAssetIds.size) return;
    setAdding(true);
    try {
      const toAdd = assetPickerItems.filter((a) => selectedAssetIds.has(a.asset_id));
      for (const asset of toAdd) {
        await brandAssetActions.addMoodboardItemFromUrl(brandId, moodboard.moodboard_id, asset.url, asset.name);
      }
      message.success(`${toAdd.length} asset${toAdd.length > 1 ? 's' : ''} added`);
      setSelectedAssetIds(new Set());
      setShowAdd(false);
      onRefresh();
    } catch { message.error('Failed to add assets'); } finally { setAdding(false); }
  };

  const handleAddUrl = async () => {
    if (!urlInput.trim()) return;
    setAdding(true);
    try {
      await brandAssetActions.addMoodboardItemFromUrl(brandId, moodboard.moodboard_id, urlInput.trim());
      setUrlInput(''); setShowAdd(false);
      message.success('Image added — uploading to CDN…');
      onRefresh();
    } catch { message.error('Failed to add image'); } finally { setAdding(false); }
  };

  const handleAddFiles = async (files) => {
    if (!files?.length) return;
    setAdding(true);
    try {
      for (const file of Array.from(files)) {
        await brandAssetActions.uploadAndAddMoodboardItem(brandId, moodboard.moodboard_id, file);
      }
      message.success(`${files.length} image${files.length > 1 ? 's' : ''} added`);
      setShowAdd(false);
      onRefresh();
    } catch { message.error('Upload failed'); } finally { setAdding(false); }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await brandAssetActions.removeMoodboardItem(brandId, moodboard.moodboard_id, itemId);
      onRefresh();
    } catch { message.error('Failed to remove image'); }
  };

  const handleSaveTitle = async () => {
    if (!titleValue.trim() || titleValue === moodboard.title) { setEditingTitle(false); return; }
    try {
      const res = await brandAssetActions.updateMoodboard(brandId, moodboard.moodboard_id, { title: titleValue.trim() });
      if (res.success) { onTitleUpdate(res.data); setEditingTitle(false); message.success('Updated'); }
    } catch { message.error('Failed to update'); setEditingTitle(false); }
  };

  const items = moodboard.items || [];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Detail Header */}
      <div style={{ padding: `${spacing.md}px ${spacing.lg}px`, borderBottom: `1px solid ${colors.border.secondary}`, display: 'flex', alignItems: 'center', gap: spacing.md }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {editingTitle ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
              <input autoFocus value={titleValue} onChange={(e) => setTitleValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTitle(); if (e.key === 'Escape') { setEditingTitle(false); setTitleValue(moodboard.title); } }} style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold, background: 'transparent', border: `1px solid ${colors.brand.primary}`, borderRadius: radius.sm, color: colors.text.primary, padding: '2px 8px', outline: 'none', width: 'auto', minWidth: 180 }} />
              <motion.button whileHover={{ opacity: 0.8 }} onClick={handleSaveTitle} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.status.success, display: 'flex' }}><Check size={16} /></motion.button>
              <motion.button whileHover={{ opacity: 0.8 }} onClick={() => { setEditingTitle(false); setTitleValue(moodboard.title); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.text.disabled, display: 'flex' }}><X size={16} /></motion.button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
              <span style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold, color: colors.text.primary }}>{moodboard.title}</span>
              <motion.button whileHover={{ opacity: 0.7 }} onClick={() => setEditingTitle(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.text.disabled, display: 'flex' }}><Edit3 size={14} /></motion.button>
            </div>
          )}
          {moodboard.description && <div style={{ fontSize: typography.fontSize.sm, color: colors.text.tertiary, marginTop: 2 }}>{moodboard.description}</div>}
        </div>
        <div style={{ fontSize: typography.fontSize.xs, color: colors.text.disabled }}>{items.length} image{items.length !== 1 ? 's' : ''}</div>
        <motion.button whileHover={{ backgroundColor: `${colors.brand.primary}20`, borderColor: colors.brand.primary }} whileTap={{ scale: 0.97 }} onClick={() => setShowAdd((v) => !v)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: 'transparent', border: `1px solid ${colors.border.primary}`, borderRadius: radius.sm, color: colors.text.secondary, fontSize: typography.fontSize.sm, cursor: 'pointer', transition: transitions.fast }}>
          <Plus size={13} /> Add Image
        </motion.button>
      </div>

      {/* Add Image Panel */}
      <AnimatePresence>
        {showAdd && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} style={{ overflow: 'hidden', borderBottom: `1px solid ${colors.border.secondary}`, background: colors.background.tertiary }}>
            <div style={{ padding: spacing.md }}>
              {/* Tabs */}
              <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.md, alignItems: 'center' }}>
                {[
                  { key: 'file', icon: <Upload size={11} />, label: 'File' },
                  { key: 'url', icon: <Link size={11} />, label: 'URL' },
                  { key: 'assets', icon: <Layers size={11} />, label: 'From Assets' },
                ].map(({ key, icon, label }) => (
                  <button key={key} onClick={() => setAddTab(key)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 12px', borderRadius: radius.sm, border: 'none', background: addTab === key ? colors.brand.primary : 'transparent', color: addTab === key ? '#fff' : colors.text.tertiary, fontSize: typography.fontSize.sm, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {icon}{label}
                  </button>
                ))}
                <motion.button whileHover={{ opacity: 0.7 }} onClick={() => { setShowAdd(false); setSelectedAssetIds(new Set()); }} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: colors.text.disabled, display: 'flex' }}>
                  <X size={15} />
                </motion.button>
              </div>

              {/* File tab */}
              {addTab === 'file' && (
                <div onClick={() => fileRef.current?.click()} style={{ border: `2px dashed ${colors.border.primary}`, borderRadius: radius.md, padding: spacing.xl, textAlign: 'center', cursor: 'pointer' }}>
                  <Upload size={24} color={colors.text.disabled} style={{ marginBottom: 8 }} />
                  <div style={{ color: colors.text.secondary, fontSize: typography.fontSize.sm }}>Drop or <span style={{ color: colors.brand.primary }}>browse</span></div>
                  <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => handleAddFiles(e.target.files)} />
                </div>
              )}

              {/* URL tab */}
              {addTab === 'url' && (
                <div style={{ display: 'flex', gap: spacing.sm }}>
                  <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddUrl()} placeholder="https://example.com/image.jpg" style={{ flex: 1, padding: '8px 12px', background: colors.background.secondary, border: `1px solid ${colors.border.primary}`, borderRadius: radius.sm, color: colors.text.primary, fontSize: typography.fontSize.sm, outline: 'none' }} />
                  <motion.button whileHover={{ opacity: 0.85 }} whileTap={{ scale: 0.97 }} onClick={handleAddUrl} disabled={adding || !urlInput.trim()} style={{ padding: '8px 18px', background: colors.brand.primary, border: 'none', borderRadius: radius.sm, color: '#fff', fontSize: typography.fontSize.sm, cursor: 'pointer', opacity: adding ? 0.6 : 1 }}>
                    {adding ? '…' : 'Add'}
                  </motion.button>
                </div>
              )}

              {/* From Assets tab */}
              {addTab === 'assets' && (
                <div>
                  {loadingAssets ? (
                    <div style={{ textAlign: 'center', color: colors.text.disabled, padding: `${spacing.lg}px 0`, fontSize: typography.fontSize.sm }}>Loading assets…</div>
                  ) : assetPickerItems.length === 0 ? (
                    <div style={{ textAlign: 'center', color: colors.text.disabled, padding: `${spacing.lg}px 0`, fontSize: typography.fontSize.sm }}>
                      No brand assets yet — upload some in the Asset Library first.
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: spacing.xs, maxHeight: 260, overflowY: 'auto', paddingRight: 2 }}>
                        {assetPickerItems.map((asset) => {
                          const sel = selectedAssetIds.has(asset.asset_id);
                          return (
                            <div
                              key={asset.asset_id}
                              onClick={() => toggleAsset(asset.asset_id)}
                              style={{ position: 'relative', aspectRatio: '1', borderRadius: radius.sm, overflow: 'hidden', cursor: 'pointer', background: colors.background.secondary, outline: sel ? `2px solid ${colors.brand.primary}` : `1px solid ${colors.border.secondary}`, outlineOffset: sel ? 1 : 0 }}
                            >
                              <img src={asset.url} alt={asset.name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', filter: sel ? 'brightness(0.55)' : 'none', transition: 'filter 0.12s' }} onError={(e) => { e.target.style.display = 'none'; }} />
                              {sel && (
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: colors.brand.primary, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Check size={12} color="#fff" strokeWidth={3} />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ marginTop: spacing.sm, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: typography.fontSize.xs, color: colors.text.disabled }}>
                          {selectedAssetIds.size > 0 ? `${selectedAssetIds.size} selected` : 'Click to select'}
                        </span>
                        <motion.button
                          whileHover={{ opacity: 0.85 }} whileTap={{ scale: 0.97 }}
                          onClick={handleAddFromAssets}
                          disabled={adding || selectedAssetIds.size === 0}
                          style={{ padding: '7px 18px', background: colors.brand.primary, border: 'none', borderRadius: radius.sm, color: '#fff', fontSize: typography.fontSize.sm, cursor: selectedAssetIds.size === 0 ? 'default' : 'pointer', opacity: adding || selectedAssetIds.size === 0 ? 0.45 : 1 }}
                        >
                          {adding ? '…' : `Add ${selectedAssetIds.size > 0 ? selectedAssetIds.size : ''} to Moodboard`}
                        </motion.button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {adding && addTab !== 'assets' && (
                <div style={{ marginTop: spacing.xs, color: colors.status.warning, fontSize: typography.fontSize.xs, textAlign: 'center' }}>Uploading to CDN & queuing caption…</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Items Grid */}
      <div style={{ flex: 1, overflowY: 'auto', padding: spacing.lg }}>
        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: spacing.md }}>
            {[...Array(6)].map((_, i) => <div key={i} style={{ aspectRatio: '4/3', borderRadius: radius.md, background: colors.surface.primary, animation: 'pulse 1.5s ease-in-out infinite' }} />)}
            <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
          </div>
        ) : items.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: spacing.xxxl, textAlign: 'center', gap: spacing.md }}>
            <div style={{ width: 56, height: 56, borderRadius: radius.full, background: `${colors.brand.secondary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Image size={24} color={colors.brand.secondary} />
            </div>
            <div style={{ color: colors.text.secondary, fontSize: typography.fontSize.md }}>No images yet</div>
            <div style={{ color: colors.text.disabled, fontSize: typography.fontSize.sm }}>Add images from files or URLs — they're re-uploaded to your CDN and auto-captioned for the AI.</div>
            <motion.button whileHover={{ opacity: 0.85 }} whileTap={{ scale: 0.97 }} onClick={() => setShowAdd(true)} style={{ padding: '8px 20px', background: colors.brand.secondary, border: 'none', borderRadius: radius.sm, color: '#fff', fontSize: typography.fontSize.sm, cursor: 'pointer' }}>
              Add Image
            </motion.button>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: spacing.md }}>
            {items.map((item) => (
              <MoodboardItemCard key={item.item_id} item={item} onRemove={() => handleRemoveItem(item.item_id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MoodboardItemCard({ item, onRemove }) {
  const [hovered, setHovered] = useState(false);
  const statusColor = captionStatusColor[item.caption_status] || '#8c8c8c';
  return (
    <motion.div whileHover={{ y: -2 }} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{ position: 'relative', borderRadius: radius.md, overflow: 'hidden', background: colors.background.secondary, border: `1px solid ${hovered ? colors.border.primary : colors.border.secondary}`, transition: transitions.fast }}>
      <div style={{ position: 'relative', aspectRatio: '4/3', overflow: 'hidden' }}>
        <img src={item.url} alt={item.name || 'Moodboard image'} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: hovered ? 'brightness(0.65)' : 'none', transition: transitions.normal }} onError={(e) => { e.target.style.display = 'none'; }} />
        <div style={{ position: 'absolute', top: 6, right: 6, width: 8, height: 8, borderRadius: radius.full, background: statusColor, border: '1.5px solid rgba(0,0,0,0.4)' }} title={`Caption: ${item.caption_status}`} />
        <AnimatePresence>
          {hovered && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={onRemove} style={{ width: 32, height: 32, borderRadius: radius.full, background: 'rgba(245,87,108,0.9)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Trash2 size={14} color="#fff" />
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {item.caption_description && (
        <div style={{ padding: '6px 8px' }}>
          <div style={{ fontSize: 10, color: colors.text.disabled, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.caption_description}</div>
        </div>
      )}
    </motion.div>
  );
}
